import React from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { FirebaseAppProvider } from "reactfire";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import { Amplify } from "@aws-amplify/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import theme from "../shared/config/theme";
import firebaseApp from "../shared/config/firebaseApp";
import FirebaseSDKProviders from "./providers/FirebaseSDKProviders";
import GlobalStyles from "./providers/GlobalStyles";
import IntlProvider from "./providers/IntlProvider";
import Root from "./router/Root";
import amplifyConfig from "../shared/config/amplifyConfig";
import { UIContextProvider } from "../shared/ui/contexts/UIContext";
import { AuthContextProvider } from "../features/auth/model";
import Head from "../shared/ui/display/Head";
import ErrorBoundary from "../shared/ui/feedback/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

Amplify.configure(amplifyConfig);

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <FirebaseAppProvider firebaseApp={firebaseApp}>
          <FirebaseSDKProviders>
            <ThemeProvider theme={theme}>
              <StyledEngineProvider injectFirst>
                <Router basename={process.env.PUBLIC_URL || "/"}>
                  <IntlProvider>
                    <Head />
                    <GlobalStyles />
                    <CssBaseline />
                    <UIContextProvider>
                      <AuthContextProvider>
                        <Root />
                      </AuthContextProvider>
                    </UIContextProvider>
                  </IntlProvider>
                </Router>
              </StyledEngineProvider>
            </ThemeProvider>
          </FirebaseSDKProviders>
        </FirebaseAppProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
